import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import drkTheme from './drkTheme'; // Modernes Theme
import drkClassicTheme from './drkClassicTheme'; // Klassisches Theme
import drkDarkTheme from './drkDarkTheme'; // Dark Theme
import { useUser } from '../components/userContext';

// Theme-Typen
export const THEME_MODERN = 'modern';
export const THEME_CLASSIC = 'classic';
export const THEME_DARK = 'dark';

// Context für Theme-Einstellungen
const ThemeContext = createContext({
    currentTheme: THEME_MODERN,
    setTheme: () => { },
    toggleTheme: () => { },
});

// Hook zum einfachen Zugriff auf das Theme
export const useThemeContext = () => useContext(ThemeContext);

// Provider-Komponente
export const ThemeContextProvider = ({ children }) => {
    const { user, settings, updateSetting } = useUser();

    // Lese Theme-Einstellung aus den Benutzereinstellungen oder fallback auf localStorage
    // Default ist jetzt THEME_CLASSIC
    const [currentTheme, setCurrentTheme] = useState(() => {
        // Prüfe zuerst, ob das Theme in den Benutzereinstellungen gespeichert ist
        if (settings && settings.theme) {
            return settings.theme;
        }

        // Fallback: Lese aus localStorage für nicht eingeloggte Benutzer oder alte Einstellungen
        const savedTheme = localStorage.getItem('drkTheme');
        if (savedTheme === THEME_MODERN) return THEME_MODERN;
        if (savedTheme === THEME_CLASSIC) return THEME_CLASSIC;
        if (savedTheme === THEME_DARK) return THEME_DARK;
        return THEME_CLASSIC; // Default ist CLASSIC
    });

    // Aktualisiere das Theme, wenn sich die Benutzereinstellungen ändern
    useEffect(() => {
        if (settings) {
            // Wenn der Benutzer eingeloggt ist und noch keine Theme-Einstellung hat,
            // Wenn es eine Theme-Einstellung gibt und sie vom aktuellen Theme abweicht
            if (settings.theme && settings.theme !== currentTheme) {
                setCurrentTheme(settings.theme);
            }
        }
    }, [settings, currentTheme, user, updateSetting]);

    // Setze Theme
    const setTheme = (theme) => {
        setCurrentTheme(theme);

        // Speichere in Benutzereinstellungen, wenn Benutzer eingeloggt ist
        if (user) {
            updateSetting('theme', theme);
        } else {
            // Fallback für nicht eingeloggte Benutzer
            localStorage.setItem('drkTheme', theme);
        }
    };

    // Toggle zwischen Themes (zyklisch: Modern → Classic → Dark → Modern)
    const toggleTheme = () => {
        let newTheme;
        if (currentTheme === THEME_MODERN) {
            newTheme = THEME_CLASSIC;
        } else if (currentTheme === THEME_CLASSIC) {
            newTheme = THEME_DARK;
        } else {
            newTheme = THEME_MODERN;
        }
        setTheme(newTheme);
    };

    // Aktuelles Theme-Objekt basierend auf der Einstellung
    const theme = currentTheme === THEME_DARK ? drkDarkTheme
        : currentTheme === THEME_CLASSIC ? drkClassicTheme
        : drkTheme;

    // Setze ein data-theme Attribut für globale CSS (z.B. :root[data-theme="dark"]) 
    useEffect(() => {
        try {
            const mode = currentTheme === THEME_DARK ? 'dark' : 'light';
            document.documentElement.setAttribute('data-theme', mode);
        } catch (e) {
            // no-op (SSR safety)
        }
    }, [currentTheme]);

    // Stelle Context-Werte bereit
    const contextValue = {
        currentTheme,
        setTheme,
        toggleTheme,
    };

    return (
        <ThemeContext.Provider value={contextValue}>
            <ThemeProvider theme={theme}>
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};

export default ThemeContextProvider;
